import classNames from 'classnames/bind';
import React, { FocusEvent, ForwardedRef, forwardRef, useState } from 'react';
import InputMask from 'react-input-mask';
import { RANGE_INPUT_MASK } from '../DateRange.constants';

import styles from './MaskedDateInput.module.scss';
import Button, { ButtonMode } from '../../Button/Button';
import Icon, { ICONS } from '../../Icon';

const cx = classNames.bind(styles);

interface MaskedDateInputProps {
  value: string;
  onInputChange: (value: string) => void;
  onFocus: () => void;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  onCaretMouseUp: () => void;
  onCaretClick: () => void;
  isDatePickerOpen: boolean;
  error: boolean;
  open: boolean;
}

const MaskedDateInput = (
  {
    value,
    error,
    onFocus,
    onBlur,
    isDatePickerOpen,
    onInputChange,
    onCaretMouseUp,
    onCaretClick,
    open,
  }: MaskedDateInputProps,
  ref: ForwardedRef<HTMLInputElement>
): React.ReactElement => {
  const [hideError, setHideError] = useState(false);

  const handleFocus = () => {
    setHideError(true);
    onFocus();
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setHideError(false);
    onBlur(e);
  };

  const showError = error && !hideError && !isDatePickerOpen;
  return (
    <div className={cx('body')} data-testid="date-input">
      <div className={cx('input-container')}>
        {showError && (
          <span className={cx('icons', 'error-icon')}>
            <Icon name={ICONS.Alert} width={12} height={12} />
          </span>
        )}
        {value !== RANGE_INPUT_MASK && (
          <Button
            className={cx('icons', 'clear-button')}
            mode={ButtonMode.TERTIARY}
            onClick={() => {
              onInputChange(RANGE_INPUT_MASK);
            }}
            dataTestId="date-input-reset"
          >
            <Icon name={ICONS.Times} />
          </Button>
        )}
        <Button
          className={cx('icons', 'filter-caret', { rotate: open })}
          mode={ButtonMode.TERTIARY}
          onMouseUp={onCaretMouseUp}
          onClick={onCaretClick}
          dataTestId="date-input-caret"
        >
          <Icon name={ICONS.CaretDown} height={10} width={10} />
        </Button>
        <InputMask
          inputRef={ref}
          className={cx('date-input', { 'is-error': showError })}
          mask="99/99/99 - 99/99/99"
          autoFocus={isDatePickerOpen}
          alwaysShowMask
          autoComplete="off"
          value={value}
          onChange={(e) => onInputChange(e.target.value)}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </div>
      {showError && (
        <div className={cx('error-text')} data-testid="date-input-error">
          Invalid date format
        </div>
      )}
    </div>
  );
};

export default forwardRef(MaskedDateInput);
